import { createClient } from '@supabase/supabase-js';
import { getSupabaseUrl, getSupabaseAnonKey, getApiUrl } from '../config/environment';

const supabase = createClient(
  getSupabaseUrl(),
  getSupabaseAnonKey()
);


// プラン定義
const PLANS = [
  {
    id: 'starter',
    name: 'スタータープラン',
    monthly_fee: 0,
    evaluation_fee: 500,
    max_users: 5,
    trial_days: 14,
    trial_evaluation_count: 10,
    is_active: true,
    stripe_price_id: 'price_starter',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'light',
    name: 'ライトプラン',
    monthly_fee: 5000,
    evaluation_fee: 300,
    max_users: 10,
    trial_days: 14,
    trial_evaluation_count: 20,
    is_active: true,
    stripe_price_id: 'price_light',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'standard',
    name: 'スタンダードプラン',
    monthly_fee: 15000,
    evaluation_fee: 200,
    max_users: 50,
    trial_days: 14,
    trial_evaluation_count: 50,
    is_active: true,
    stripe_price_id: 'price_standard',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'enterprise',
    name: 'エンタープライズプラン',
    monthly_fee: null,
    evaluation_fee: 150,
    max_users: null,
    trial_days: 30,
    trial_evaluation_count: 100,
    is_active: true,
    stripe_price_id: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

const SUBSCRIPTION_TEMPLATE = {
  id: 'sub_mock_123',
  plan_id: 2, // ライトプラン
  status: 'active',
  current_period_start: new Date(
    Date.now() - 15 * 24 * 60 * 60 * 1000
  ).toISOString(), // 15日前
  current_period_end: new Date(
    Date.now() + 15 * 24 * 60 * 60 * 1000
  ).toISOString(), // 15日後
  stripe_subscription_id: 'sub_stripe_mock',
  stripe_customer_id: 'cus_mock_123', // 追加
  plans: PLANS.find(p => p.id === 'light'),
  tenants: {
    id: 'comp_mock_123',
    name: 'サンプル株式会社',
    email: 'billing@sample.com',
    phone: '03-1234-5678',
    address: '東京都渋谷区サンプル1-2-3',
    contact_person: '田中太郎',
    stripe_customer_id: 'cus_mock_123',
  },
};

const MOCK_USAGE = {
  summary: {
    assessment: 25,
    evaluation: 25,
    report: 5,
  },
  daily: Array.from({ length: 30 }, (_, i) => ({
    date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0],
    assessment_count: Math.floor(Math.random() * 5),
    evaluation_count: Math.floor(Math.random() * 5),
  })),
};

const MOCK_INVOICES = [
  {
    id: 'inv_mock_123',
    invoice_number: 'INV-2024-001',
    amount: 12500,
    status: 'paid',
    issued_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    due_date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    paid_at: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    invoice_items: [
      {
        description: 'ライトプラン月額料金',
        quantity: 1,
        unit_price: 5000,
        amount: 5000,
      },
      {
        description: '評価実行料金 (25回)',
        quantity: 25,
        unit_price: 300,
        amount: 7500,
      },
    ],
  },
  // TEST: Add real Stripe invoice for PDF download testing with correct UUID
  {
    id: 'c927629c-2680-402a-94e0-42336fe3b429', // Real database UUID
    invoice_number: 'INV-2024-10-FINAL',
    amount: 1100, // 1000 + 100 tax
    status: 'paid',
    issued_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    paid_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    invoice_items: [
      {
        description: 'Test competency assessment',
        quantity: 1,
        unit_price: 1000,
        amount: 1000,
      },
      {
        description: 'Tax (10%)',
        quantity: 1,
        unit_price: 100,
        amount: 100,
      },
    ],
  },
];


export class BillingAPI {
  // プラン一覧取得
  static async getPlans() {

    try {
      // Supabase接続テスト
      const { data: connectionTest, error: connectionError } = await supabase
        .from('plans')
        .select('count', { count: 'exact', head: true });

      if (connectionError) {

        if (connectionError.code === 'PGRST116') {
          throw new Error(
            '課金システムテーブル（plans）が見つかりません。データベースのセットアップが必要です。'
          );
        }

        if (connectionError.message.includes('Failed to fetch')) {
          throw new Error(
            'Supabaseサーバーに接続できません。ネットワーク接続を確認してください。'
          );
        }

        throw new Error(`データベースエラー: ${connectionError.message}`);
      }

      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .eq('is_active', true)
        .order('monthly_fee');

      if (error) {
        throw new Error(`プラン取得エラー: ${error.message}`);
      }

      if (!data || data.length === 0) {
        throw new Error(
          '利用可能なプランが見つかりません。管理者にお問い合わせください。'
        );
      }

      return data;
    } catch (error) {

      // ネットワークエラーかどうかを判定
      if (
        error.message.includes('Failed to fetch') ||
        error.name === 'TypeError'
      ) {
        throw new Error(
          'ネットワーク接続エラー: Supabaseサーバーに接続できません。インターネット接続を確認してください。'
        );
      }

      // 既知のエラーメッセージをそのまま返す
      if (
        error.message.includes('課金システムテーブル') ||
        error.message.includes('プラン取得エラー') ||
        error.message.includes('利用可能なプランが見つかりません')
      ) {
        throw error;
      }

      // その他のエラー
      throw new Error(`予期しないエラーが発生しました: ${error.message}`);
    }
  }

  // Stripe Checkoutセッション作成
  static async createCheckoutSession(request) {


    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('認証トークンが見つかりません。ログインしてください。');
      }


      const response = await fetch(
        `${getApiUrl()}/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(request),
        }
      );


      if (!response.ok) {
        const errorText = await response.text();

        let errorMessage;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorText;
        } catch {
          errorMessage = errorText;
        }

        if (response.status === 401) {
          throw new Error('認証エラー: 再度ログインしてください');
        } else if (response.status === 404) {
          throw new Error(
            'Edge Function が見つかりません。管理者にお問い合わせください。'
          );
        } else if (errorMessage.includes('Stripe secret key not configured')) {
          throw new Error(
            'Stripe設定エラー: 管理者に連絡してください（APIキー未設定）'
          );
        } else {
          throw new Error(`決済処理エラー: ${errorMessage}`);
        }
      }

      const data = await response.json();

      if (!data.url) {
        throw new Error('決済URLが生成されませんでした');
      }

      if (data.url.includes('mock-session')) {
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  static async getUserSubscription() {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    
    if (!user) {
      return null;
    }

    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        *,
        tenants:tenant_id(*),
        plans:plan_id(*)
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      return null;
    }

    return data;
  } catch (error) {
    return null;
  }
}

  // 利用状況取得
  static async getUsage(startDate, endDate, usageType) {

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('認証が必要です。ログインしてください。');
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (usageType) params.append('usage_type', usageType);

    const response = await fetch(
      `${getApiUrl()}/record-usage?${params}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Usage fetch failed');
    }

    return response.json();
  }

  // 請求書一覧取得
  static async getInvoices() {

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('認証が必要です。ログインしてください。');

    // サブスクリプションを通じて請求書を取得
    const { data: subscription } = await supabase
      .from('subscriptions')
      .select('id')
      .eq('user_id', user.id)
      .single();

    if (!subscription) return [];

    const { data, error } = await supabase
      .from('invoices')
      .select(
        `
        *,
        invoice_items(*)
      `
      )
      .eq('subscription_id', subscription.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  // 利用量記録（評価完了時に自動で呼ばれる）
  static async recordUsage(
    usageType = 'assessment',
    quantity = 1,
    metadata = {}
  ) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const response = await fetch(
      `${getApiUrl()}/record-usage`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          usageType,
          quantity,
          metadata,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Usage recording failed');
    }

    return response.json();
  }

  // 企業情報の取得
  static async getCompanyInfo() {

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: subscription } = await supabase
      .from('subscriptions')
      .select(
        `
        tenants:tenant_id(*)
      `
      )
      .eq('user_id', user.id)
      .single();

    return subscription?.tenants || null;
  }

  // 企業情報の更新
  static async updateCompanyInfo(companyInfo) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: subscription } = await supabase
      .from('subscriptions')
      .select('tenant_id')
      .eq('user_id', user.id)
      .single();

    if (!subscription) throw new Error('Subscription not found');

    const { data, error } = await supabase
      .from('tenants')
      .update({
        ...companyInfo,
        updated_at: new Date().toISOString(),
      })
      .eq('id', subscription.tenant_id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // サブスクリプションのキャンセル
  static async cancelSubscription(subscriptionId) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const response = await fetch(
      `${getApiUrl()}/cancel-subscription`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ subscriptionId }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Subscription cancellation failed');
    }

    return response.json();
  }

  // 請求書のPDFダウンロード
  static async downloadInvoicePDF(invoiceId) {
    try {
      
      // データベースから stripe_invoice_id を取得
      const { data: invoiceData, error: dbError } = await supabase
        .from('invoices')
        .select('stripe_invoice_id')
        .eq('id', invoiceId)
        .single();

      if (dbError || !invoiceData) {
        throw new Error('請求書が見つかりません');
      }

      if (!invoiceData.stripe_invoice_id) {
        throw new Error('Stripe請求書IDが見つかりません');
      }


      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('認証が必要です。ログインしてください。');
      }


      // 本番環境APIを使用
      const response = await fetch(
        `${getApiUrl()}/invoice/download-pdf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ invoice_id: invoiceData.stripe_invoice_id }),
        }
      );


      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorText;
        } catch {
          errorMessage = errorText;
        }
        throw new Error(errorMessage || 'PDF download failed');
      }

      const responseData = await response.json();
      
      const { url } = responseData;
      if (!url) {
        throw new Error('PDF URL not found');
      }
      
      
      // 新しいタブでPDFを開く
      window.open(url, '_blank');
      
    } catch (error) {
      throw error;
    }
  }

  // カード情報管理用セットアップセッション作成
  static async createSetupSession(customerId, successUrl, cancelUrl) {

    const {
      data: { session },
    } = await supabase.auth.getSession();

    const response = await fetch(
      `${getApiUrl()}/create-setup-session`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          customer_id: customerId,
          success_url: successUrl,
          cancel_url: cancelUrl,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Setup session creation failed');
    }

    return response.json();
  }

  // Customer Portal セッション作成
  static async createPortalSession(customerId, returnUrl) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const response = await fetch(
      `${getApiUrl()}/create-portal-session`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          customer_id: customerId,
          return_url: returnUrl,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Portal session creation failed');
    }

    return response.json();
  }
}

// Stripe決済リダイレクト用URLはHashRouter対応のため「#/」付きで生成してください
// 例: const successUrl = `${window.location.origin}/#/billing/success`;
